
import { FC, useState } from 'react';
import BtButton from '../bt-button/bt-button';
import Card from '../../components/shared/Card/card';
import Tabs from '../../components/shared/Tabs';
import { useTranslation } from 'react-i18next';
import styles from './compare-credit-services.module.scss';
import TicLogo from '../../assets/svg/logo-tic.svg';
import SynaLogo from '../../assets/svg/logo-syna.svg';
import CreditsafeLogo from '../../assets/images/logo-creditsafe.png';
import { TicSignup } from './tic-signup';
import CheckIcon from '@material-ui/icons/Check';

interface feature {
  [key: string]: boolean | 'partial';
}

interface CreditProvider {
  id: string;
  name: string;
  logo: string;
  tagline: string;
  pricing: {
    amount: number | string;
    unit: string;
    type: 'fixed' | 'quote';
  };
  features: { [key: string]: feature };
  highlights: string[];
  buttonText: string;
  actionComponent?: React.ReactNode;
  activeSubscription: boolean;
  showActionComponent: boolean;
  email?: string;
  apiKey?: string;
}

const creditProviders: CreditProvider[] = [
  {
    id: 'tic',
    name: 'The Intelligence Company',
    logo: TicLogo,
    tagline: 'Omfattande företagsinformation med djup analys',
    pricing: {
      amount: 295,
      unit: 'kr/mån',
      type: 'fixed',
    },
    features: {
      'Sökfunktioner': {
        'Företag': true,
        'Personuppgifter': true,
        'Fastigheter': true,
        'Fordon': true,
        'Adresser': true,
      },
      'Rapporter': {
        'Årsredovisningar': true,
        'Sanktionslistor': true,
      },
      'Basinformation': {
        'Företag': true,
        'Privatpersoner': true,
        'Fastigheter': true,
      },
      'Tekniska detaljer': {
        'Realtidsdata': true,
        'API-integration': true,
      },
    },
    highlights: ['Mest omfattande databas', 'Snabb implementation', 'Svensk support'],
    buttonText: 'Skapa konto',
    activeSubscription: false,
    showActionComponent: false
  },
  {
    id: 'syna',
    name: 'SYNA',
    logo: SynaLogo,
    tagline: 'Flexibla lösningar för alla företagsstorlekar',
    pricing: {
      amount: 'Offert',
      unit: '',
      type: 'quote',
    },
    features: {
      'Sökfunktioner': {
        'Företag': true,
        'Personuppgifter': true,
        'Fastigheter': 'partial',
        'Fordon': true,
        'Adresser': true,
      },
      'Rapporter': {
        'Årsredovisningar': true,
        'Sanktionslistor': true,
      },
      'Basinformation': {
        'Företag': true,
        'Privatpersoner': true,
        'Fastigheter': 'partial',
      },
      'Tekniska detaljer': {
        'Realtidsdata': 'partial',
        'API-integration': true,
      },
    },
    highlights: ['Anpassade priser', 'Personlig service', 'Flexibla avtal'],
    buttonText: 'Kontakta säljare',
    activeSubscription: false,
    showActionComponent: false
  },
  {
    id: 'creditsafe',
    name: 'Creditsafe',
    logo: CreditsafeLogo,
    tagline: 'Global täckning med lokal expertis',
    pricing: {
      amount: 'Offert',
      unit: '',
      type: 'quote',
    },
    features: {
      'Sökfunktioner': {
        'Företag': true,
        'Personuppgifter': true,
        'Fastigheter': false,
        'Fordon': true,
        'Adresser': true,
      },
      'Rapporter': {
        'Årsredovisningar': true,
        'Sanktionslistor': true,
      },
      'Basinformation': {
        'Företag': true,
        'Privatpersoner': true,
        'Fastigheter': false,
      },
      'Tekniska detaljer': {
        'Realtidsdata': true,
        'API-integration': true,
      },
    },
    highlights: ['Global databas', 'Stark säkerhet', 'Etablerad aktör'],
    buttonText: 'Kontakta säljare',
    activeSubscription: false,
    showActionComponent: false
  },
];

const featureCategories = {
  'Sökfunktioner': ['Företag', 'Personuppgifter', 'Fordon', 'Adresser', 'Fastigheter'],
  'Rapporter': ['Årsredovisningar', 'Sanktionslistor'],
  'Basinformation': ['Företag', 'Privatpersoner', 'Fastigheter'],
  'Tekniska detaljer': ['API-integration', 'Realtidsdata'],
};
const mainFeatures = {
  'Sök företag': ['Sökfunktioner', 'Företag'],
  'Sök personer': ['Sökfunktioner', 'Personuppgifter'],
  'Sök fastigheter': ['Sökfunktioner', 'Fastigheter'],
  'Sök fordon': ['Sökfunktioner', 'Fordon'],
  'Sök adresser': ['Sökfunktioner', 'Adresser'],
  'Sök årsredovisningar': ['Rapporter', 'Årsredovisningar'],
};

type Props = {
  hasCreditsafe: boolean;
  hasSyna: boolean;
  hasTic: boolean;
}

const CompareCreditServices: FC<Props> = ({hasCreditsafe, hasSyna, hasTic}) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'supplierControls.creditServices' });
  const [selectedTab, setSelectedTab] = useState(0);
  const [providers, setProviders] = useState<CreditProvider[]>(creditProviders.map((provider) => ({
    ...provider,
    activeSubscription: (provider.id === 'creditsafe' && hasCreditsafe) || (provider.id === 'syna' && hasSyna) || (provider.id === 'tic' && hasTic)
  })));

  const onProviderButtonClick = (e: React.MouseEvent, provider: CreditProvider) => {
    //console.log('onProviderButtonClick', provider);
    e.stopPropagation();
    if (provider.id === 'tic') {
      provider.showActionComponent = true;
      setProviders([...providers]);
    } else if (provider.id === 'syna') {
      // TODO: Implement Syna CTA
    } else if (provider.id === 'creditsafe') {
      // TODO: Implement Creditsafe CTA
    }
  };

  const onCompareButtonClick = (e: React.MouseEvent, provider: CreditProvider) => {
    //console.log('onCompareButtonClick', provider);
    if (provider.id === 'tic') {
      setSelectedTab(0);
      onProviderButtonClick(e, provider);
    }
  };

  const onCancelClick = (e: React.MouseEvent, provider: CreditProvider) => {
    e.stopPropagation();
    provider.showActionComponent = false;
    setProviders([...providers]);
    //console.log('onCancelClick', provider);
  };

  const onSubscribe = (provider: CreditProvider, email: string, apiKey: string) => {
    provider.activeSubscription = true;
    provider.email = email;
    provider.apiKey = apiKey;
    provider.showActionComponent = false;
    setProviders([...providers]);
    //console.log('onSubscribe', provider);
  };

  const Feature = ({ text, status }: { text?: string, status: boolean | 'partial' }) => {
    return (
      <div className={`${styles.feature} ${styles[`feature--${status}`]}`}>
        <div className={styles.icon}>
          { status === 'partial' ? '-' : (status ? '✓' : '✗' )}
        </div>
        { text && <span className={styles.text}>{text}</span> }
      </div>
    );
  };

  return <main className={styles['compare-credit-services']}>
    <h1>Jämför kreditupplysningar</h1>
    <p className={styles.intro}>
      Välj den kreditupplysningsleverantör som passar dina behov bäst. Jämför funktioner, priser och välj direkt.
    </p>
    <nav className={styles['tabs-container']}>
      <Tabs
        tabs={[
          t('tabs.overview','Översikt'),
          t('tabs.compare', 'Jämför'),
        ]}
        selected={selectedTab}
        onChange={setSelectedTab}
      />
    </nav>
    { selectedTab === 0 ? (
      <section className={styles.overview}>
        {providers.map((provider) => (
          <Card key={provider.id+provider.showActionComponent} className={styles['provider-card']}>
            <h2>
              <img src={provider.logo} height="40" className={styles.logo} />
              {provider.name}
            </h2>
            <p>{provider.tagline}</p>

            <div className={styles.price}>
              { !provider.activeSubscription &&
                <>
                  {provider.pricing.amount}
                  {provider.pricing.unit && (
                    <span className={styles.unit}>{provider.pricing.unit}</span>
                  )}
                </>
              }
            </div>
            { provider.id === 'tic' && <>
              { provider.showActionComponent && <TicSignup onSubscribe={(email, apiKey) => onSubscribe(provider, email, apiKey)} onCancel={(e) => onCancelClick(e, provider) } /> }
              { provider.email && provider.apiKey &&
                <div className={styles.signup}>
                  <h3><span className={styles.congrats}>Grattis!</span></h3>
                  <p>Du har nu ett aktivt abonnemang hos Tic.</p>
                  <p>Ditt abonnemang är kopplat till: {provider.email}</p>
                  <p>api-key: {provider.apiKey}</p>
                </div>
              }
            </> }
            { !provider.showActionComponent && !(provider.apiKey && provider.email) && <>
              <div className={styles['usps-and-feats']}>
                <div className={styles.usps}>
                  <ul>
                    {provider.highlights.map((highlight, index) => (
                      <li key={index}>
                        <Feature text={highlight} status={true} />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles['features']}>
                  <h3>Huvudfunktioner</h3>
                  <div>
                    {Object.entries(mainFeatures)
                      .map(([name, [category, feature]]) => (
                        <Feature key={name} text={name} status={provider.features[category][feature]} />
                      ))}
                  </div>
                </div>
              </div>
            </> }
            { !provider.showActionComponent &&
              <div className={styles['button-container']}>
                { provider.activeSubscription ?
                  <BtButton color="white" disabled leftIcon={<CheckIcon />} class={styles['active-subscription']}>
                    <div className={styles.text}>Aktivt abonnemang</div>
                  </BtButton>
                :
                  <BtButton onClick={(e) => onProviderButtonClick(e, provider) }>
                    <div className={styles.text}>{provider.buttonText}</div>
                  </BtButton>
                }
              </div>
            }
          </Card>
        ))}
      </section>
    ) : (
      <section className={styles.compare}>
        <table>
          <thead>
            <tr>
              <th></th>
              {providers.map((provider) => (
                <th key={provider.id} scope="col">
                  <img src={provider.logo} height="20" className={styles.logo} />
                  {provider.name}
                  <div className={styles.price}>
                  { !provider.activeSubscription &&
                    <>
                      {provider.pricing.amount}
                      {provider.pricing.unit && (
                        <span className={styles.unit}>{provider.pricing.unit}</span>
                      )}
                    </>
                  }
                  </div>
                  { provider.activeSubscription ?
                    <BtButton size="small" color="white" disabled leftIcon={<CheckIcon fontSize="small"/>} class={styles['active-subscription']}>
                      <div className={styles.text}>Aktivt abonnemang</div>
                    </BtButton>
                  :
                    <BtButton size="small" class={styles.button} onClick={(e) => onCompareButtonClick(e, provider) }>
                      <div className={styles.text}>{provider.buttonText}</div>
                    </BtButton>
                  }
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(featureCategories).map(([category, features]) => (
              <>
                <tr key={category}>
                  <th scope="col" colSpan={providers.length + 1}>
                    {category}
                  </th>
                </tr>
                {features.map((feature) => (
                  <tr key={feature}>
                    <th scope="row">{feature}</th>
                    {providers.map((provider) => (
                      <td key={provider.id}>
                        <Feature status={provider.features[category][feature]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </section>
    )}
  </main>
};

export default CompareCreditServices;
